import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { optimizeImage } from "@/lib/imageUrl";
import { Skeleton } from "@/components/ui/skeleton";
import { UserProfilePopup } from "@/components/UserProfilePopup";
import { BusinessOwnerBadge } from "@/components/BusinessOwnerBadge";
import { ArrowRight, UserPlus, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { setProfileQueue } from "@/lib/profileQueue";

interface OpenUser {
  id: string;
  first_name: string | null;
  avatar_url: string | null;
  district: string | null;
  role: string | null;
  is_business_owner?: boolean | null;
  company_name?: string | null;
}

const roleLabelShort = (role: string | null): string | null => {
  switch (role) {
    case 'parent': return 'Parent';
    case 'nanny': return 'Nanny';
    case 'childminder': return 'Childminder';
    default: return null;
  }
};

interface OpenToMeetStripProps {
  onSeeAll?: () => void;
  homepage?: boolean;
}

const MAX_VISIBLE = 24;

export const OpenToMeetStrip = ({ onSeeAll, homepage = false }: OpenToMeetStripProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<OpenUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setUsers([]);
      setLoading(false);
      return;
    }


    let cancelled = false;
    const fetchOpen = async () => {
      setLoading(true);
      try {
        const { data } = await supabase
          .from("public_profiles")
          .select("id, first_name, avatar_url, district, role, open_to_meet, open_to_meet_since, is_business_owner, company_name")
          .eq("open_to_meet", true)
          .order("open_to_meet_since", { ascending: false, nullsFirst: false })
          .limit(MAX_VISIBLE + 40);

        if (cancelled) return;
        const eligible = (data || []).filter(
          (u: any) => u.id && u.first_name && u.avatar_url && (!user || u.id !== user.id) && u.role !== 'other'
        );

        // Prioritise users with a district
        const withDistrict = eligible.filter((u: any) => !!u.district);
        const withoutDistrict = eligible.filter((u: any) => !u.district);

        // Interleave by role to get a mix (parent, nanny, childminder, other)
        const roleOrder = ['parent', 'nanny', 'childminder', 'other'];
        const interleaveByRole = (list: any[]) => {
          const buckets: Record<string, any[]> = {};
          roleOrder.forEach((r) => { buckets[r] = []; });
          list.forEach((u) => {
            const key = roleOrder.includes(u.role) ? u.role : 'other';
            buckets[key].push(u);
          });
          const result: any[] = [];
          let added = true;
          while (added) {
            added = false;
            for (const r of roleOrder) {
              const next = buckets[r].shift();
              if (next) { result.push(next); added = true; }
            }
          }
          return result;
        };

        let ordered = [
          ...interleaveByRole(withDistrict),
          ...interleaveByRole(withoutDistrict),
        ];

        // Guarantee role diversity in the homepage top-5 slice: ensure at
        // least one nanny and one childminder appear if any exist in the pool.
        const HERO_SLOTS = 5;
        const ensureRoleInHero = (role: string) => {
          const heroHas = ordered.slice(0, HERO_SLOTS).some((u: any) => u.role === role);
          if (heroHas) return;
          const idx = ordered.findIndex((u: any, i: number) => i >= HERO_SLOTS && u.role === role);
          if (idx === -1) return;
          const [pick] = ordered.splice(idx, 1);
          // Insert at the last hero slot, pushing the previous last out of hero.
          ordered.splice(HERO_SLOTS - 1, 0, pick);
        };
        ensureRoleInHero('nanny');
        ensureRoleInHero('childminder');

        ordered = ordered.slice(0, MAX_VISIBLE);

        setUsers(ordered as OpenUser[]);

      } catch (err) {
        console.error("Error loading open-to-meet users:", err);
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOpen();
    return () => { cancelled = true; };
  }, [homepage, user?.id]);

  if (homepage && !user) {
    return (
      <section
        aria-label="Meet local parents and carers"
        className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-bright-fuschia py-16 text-primary-foreground sm:py-20 lg:py-24"
      >
        <div className="absolute -left-20 -top-24 h-72 w-72 rounded-full bg-primary-foreground/10 blur-3xl" />
        <div className="absolute -bottom-24 -right-20 h-80 w-80 rounded-full bg-light-fuschia/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <div className="mb-4 flex items-center gap-3">
                <Users className="h-8 w-8 shrink-0" />
                <h2 className="text-3xl sm:text-4xl font-bold leading-tight">Open to meet near you</h2>
              </div>
              <p className="text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
                Join local parents and carers who are open to a chat, a coffee, or an easy playdate nearby.
              </p>
            </div>
            <div className="flex -space-x-3" aria-hidden="true">
              {["M", "S", "A", "J"].map((initial, index) => (
                <Avatar
                  key={initial}
                  className="h-12 w-12 border-2 border-primary-foreground shadow-md sm:h-14 sm:w-14"
                >
                  <AvatarFallback className={`text-sm font-bold text-primary ${index % 2 === 0 ? "bg-light-fuschia" : "bg-primary-foreground"}`}>
                    {initial}
                  </AvatarFallback>
                </Avatar>
              ))}
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-primary-foreground bg-primary-foreground/20 shadow-md sm:h-14 sm:w-14">
                <Users className="h-5 w-5" />
              </div>
            </div>
          </div>
          <div className="mt-10 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <Button
              onClick={() => navigate("/auth?mode=signup")}
              size="lg"
              className="bg-primary-foreground px-8 py-6 text-base font-semibold text-primary shadow-lg hover:bg-primary-foreground/90"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Create a free account
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/auth")}
              size="lg"
              className="border border-primary-foreground/20 px-8 py-6 text-base font-semibold text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              Already a member? Sign in
            </Button>
          </div>
        </div>
      </section>
    );
  }
  if (!user) return null;


  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-4 w-40" />
        <div className="flex gap-3">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-10 rounded-full shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  if (users.length === 0) return null;

  if (homepage) {
    return (
      <section
        aria-label="Parents and carers open to meet"
        className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-bright-fuschia py-16 text-primary-foreground sm:py-20 lg:py-24"
      >
        <div className="absolute -left-20 -top-24 h-72 w-72 rounded-full bg-primary-foreground/10 blur-3xl" />
        <div className="absolute -bottom-24 -right-20 h-80 w-80 rounded-full bg-light-fuschia/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center">
          <div className="max-w-2xl">
            <div className="mb-4 flex items-center justify-center gap-3">
              <Users className="h-8 w-8 shrink-0" />
              <h2 className="text-3xl sm:text-4xl font-bold leading-tight">Open to meet near you</h2>
            </div>
            <p className="text-base text-primary-foreground/85 sm:text-lg">
              Browse local profiles, find people nearby, and start a friendly conversation.
            </p>
          </div>

          <div className="mt-8 flex gap-4 overflow-x-auto px-1 py-2 scrollbar-hide max-w-full">
            {users.slice(0, 5).map((u) => (
              <UserProfilePopup key={u.id} userId={u.id}>
                <button
                  type="button"
                  aria-label={`Open ${u.first_name}'s profile`}
                  onClick={() => setProfileQueue(users.slice(0, 5).map((x) => x.id))}
                  className="group flex w-28 shrink-0 flex-col gap-2 sm:w-32"
                >
                  <div className="relative w-full overflow-hidden rounded-2xl border-2 border-primary-foreground/80 shadow-lg transition-all group-hover:-translate-y-1 group-hover:shadow-xl" style={{ aspectRatio: "9 / 16" }}>
                    {u.avatar_url ? (
                      <img src={optimizeImage(u.avatar_url, { width: 256, height: 456, quality: 70, resize: "cover" })} alt={u.first_name || ""} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-primary-foreground text-3xl font-semibold text-primary">
                        {(u.first_name || "?").charAt(0)}
                      </div>
                    )}
                    <div className="absolute left-1.5 top-1.5">
                      <BusinessOwnerBadge
                        isBusinessOwner={u.is_business_owner}
                        role={u.role}
                        variant="compact"
                      />
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent p-2 text-left">
                      <div className="line-clamp-1 text-sm font-semibold leading-tight text-white">
                        {u.first_name}
                      </div>
                      {u.district && (
                        <div className="line-clamp-1 text-[11px] leading-tight text-white/85 capitalize">
                          {u.district}
                        </div>
                      )}
                      {roleLabelShort(u.role) && (
                        <div className="line-clamp-1 text-[11px] leading-tight text-white/70">
                          {roleLabelShort(u.role)}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              </UserProfilePopup>
            ))}
          </div>

          <Button
            onClick={onSeeAll}
            size="lg"
            className="mt-10 bg-primary-foreground px-8 py-6 text-base font-semibold text-primary shadow-lg hover:bg-primary-foreground/90"
          >
            Browse local profiles
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

      </section>
    );
  }

  return (
    <section
      aria-label="Parents and carers open to meet"
      className="py-2 space-y-3"
    >
      <div className="text-center">
        <h2 className="text-lg sm:text-xl font-semibold text-foreground">
          Open to meet near you
        </h2>
      </div>

      <div className="flex gap-4 overflow-x-auto scrollbar-hide justify-start sm:justify-center px-1 py-1">
        {users.map((u) => (
          <UserProfilePopup key={u.id} userId={u.id}>
            <button
              type="button"
              aria-label={`Open ${u.first_name}'s profile`}
              onClick={() => setProfileQueue(users.map((x) => x.id))}
              className="flex flex-col shrink-0 w-28 sm:w-32 group"
            >
              <div className="relative w-full overflow-hidden rounded-2xl ring-2 ring-primary/20 group-hover:ring-primary/60 shadow-md transition-all" style={{ aspectRatio: "9 / 16" }}>
                {u.avatar_url ? (
                  <img src={optimizeImage(u.avatar_url, { width: 256, height: 456, quality: 70, resize: "cover" })} alt={u.first_name || ""} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted text-3xl font-semibold text-foreground">
                    {(u.first_name || "?").charAt(0)}
                  </div>
                )}
                <div className="absolute left-1.5 top-1.5">
                  <BusinessOwnerBadge
                    isBusinessOwner={u.is_business_owner}
                    role={u.role}
                    variant="compact"
                  />
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent p-2">
                  <div className="line-clamp-1 text-sm font-semibold leading-tight text-white">
                    {u.first_name}
                  </div>
                  {u.district && (
                    <div className="line-clamp-1 text-[11px] leading-tight text-white/85 capitalize">
                      {u.district}
                    </div>
                  )}
                  {roleLabelShort(u.role) && (
                    <div className="line-clamp-1 text-[11px] leading-tight text-white/70">
                      {roleLabelShort(u.role)}
                    </div>
                  )}
                </div>
              </div>
            </button>
          </UserProfilePopup>
        ))}
      </div>

    </section>
  );
};

